import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
import { formatCoins, formatUsername, parseTipChatBody } from '@fetmode/utils';
import { Button } from '../Button/index.js';
const EMOJI_SET = [
    '😀',
    '😍',
    '😘',
    '😜',
    '🤤',
    '🔥',
    '💋',
    '💕',
    '😈',
    '🫣',
    '😏',
    '👀',
    '✨',
    '🖤',
    '💜',
    '💦',
    '🍑',
    '🍆',
    '👅',
    '🙌',
    '👏',
    '💯',
    '🎉',
    '🍀',
];
function rankClass(rank) {
    return `show-chat-panel__badge show-chat-panel__badge--${rank.toLowerCase()}`;
}
function UserChatBadges({ message }) {
    const parts = [];
    if (message.kingSlot) {
        parts.push(_jsxs("span", { className: "show-chat-panel__badge show-chat-panel__badge--king", children: ["K", String(message.kingSlot)] }, "king"));
    }
    if (message.rank) {
        parts.push(_jsx("span", { className: rankClass(message.rank), title: message.rank, "aria-label": message.rank }, "rank"));
    }
    if (message.isModerator) {
        parts.push(_jsx("span", { className: "show-chat-panel__badge show-chat-panel__badge--mod", children: "M" }, "mod"));
    }
    if (parts.length === 0) {
        return null;
    }
    return _jsx("span", { className: "show-chat-panel__badges", children: parts });
}
function kindClass(kind) {
    if (kind === 'SYSTEM') {
        return 'show-chat-panel__item show-chat-panel__item--system';
    }
    if (kind === 'ENTRANCE') {
        return 'show-chat-panel__item show-chat-panel__item--entrance';
    }
    if (kind === 'TIP') {
        return 'show-chat-panel__item show-chat-panel__item--tip';
    }
    return 'show-chat-panel__item';
}
export function ShowChatPanel({ messages, messageText, onMessageTextChange, onSend, isSending = false, sendError = null, canSend, emptyPrompt = '', inputPlaceholder, sendLabel, sendingLabel, deleteLabel = 'Remove', onDeleteMessage, formatTipLine, footer, composerActions, layout = 'default', renderMessage, currentUserId, voteKickLabel = 'Vote to kick', onVoteKick, }) {
    const [emojiOpen, setEmojiOpen] = useState(false);
    const pickerId = useId();
    const emojiWrapRef = useRef(null);
    const messagesRef = useRef(null);
    useEffect(() => {
        const scroller = messagesRef.current;
        if (!scroller) {
            return;
        }
        // Scroll the chat pane only. scrollIntoView scrolls the page and makes iOS
        // glitch the live video when a message is sent.
        scroller.scrollTop = scroller.scrollHeight;
    }, [messages.length]);
    useEffect(() => {
        if (!emojiOpen) {
            return;
        }
        const onPointerDown = (event) => {
            if (!emojiWrapRef.current?.contains(event.target)) {
                setEmojiOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [emojiOpen]);
    const insertEmoji = (emoji) => {
        if (!canSend) {
            return;
        }
        onMessageTextChange(`${messageText}${emoji}`);
        setEmojiOpen(false);
    };
    return (_jsxs("div", { className: layout === 'room' ? 'show-chat-panel show-chat-panel--room' : 'show-chat-panel', children: [_jsx("div", { ref: messagesRef, className: "show-chat-panel__messages", children: messages.length === 0 ? (emptyPrompt ? (_jsx("p", { className: "show-chat-panel__empty", children: emptyPrompt })) : null) : (_jsx("ul", { className: "show-chat-panel__list", children: messages.map((message) => (_jsx("li", { className: kindClass(message.kind), children: renderMessage ? (renderMessage(message)) : message.kind === 'TIP' ? (_jsx("span", { children: (() => {
                                const parsed = parseTipChatBody(message.body);
                                const amountLabel = formatCoins(parsed.amountCoins);
                                const line = formatTipLine
                                    ? formatTipLine({
                                        username: message.username,
                                        amountLabel,
                                        message: parsed.message,
                                    })
                                    : `${formatUsername(message.username)} tipped ${amountLabel}${parsed.message ? ` — ${parsed.message}` : ''}`;
                                return line;
                            })() })) : message.kind === 'USER' ? (_jsxs(_Fragment, { children: [_jsx(UserChatBadges, { message: message }), _jsx("strong", { children: formatUsername(message.username) }), ' ', _jsx("span", { children: message.body }), onDeleteMessage ? (_jsx("button", { type: "button", className: "show-chat-panel__delete", "aria-label": deleteLabel, onClick: () => {
                                        onDeleteMessage(message.id);
                                    }, children: "\u00D7" })) : null, onVoteKick && message.userId !== currentUserId ? (_jsx("button", { type: "button", className: "show-chat-panel__vote", onClick: () => {
                                        onVoteKick(message.userId);
                                    }, children: voteKickLabel })) : null] })) : (_jsx("span", { children: message.body })) }, message.id))) })) }), footer ? _jsx("div", { className: "show-chat-panel__footer", children: footer }) : null, composerActions && composerActions.length > 0 ? (_jsx("div", { className: "show-chat-panel__actions", role: "group", "aria-label": "Chat actions", children: composerActions.map((action) => (_jsx("button", { type: "button", className: "show-chat-panel__action", disabled: action.disabled || !canSend || isSending, onClick: action.onClick, children: action.label }, action.id))) })) : null, _jsxs("div", { className: "show-chat-panel__composer", children: [_jsxs("div", { className: "show-chat-panel__emoji-wrap", ref: emojiWrapRef, children: [_jsx("button", { type: "button", className: "show-chat-panel__emoji-btn", "aria-label": "Emoji", "aria-expanded": emojiOpen, "aria-controls": pickerId, disabled: !canSend || isSending, onClick: () => {
                                    setEmojiOpen((open) => !open);
                                }, children: "\uD83D\uDE0A" }), emojiOpen ? (_jsx("div", { id: pickerId, className: "show-chat-panel__emoji-picker", role: "listbox", "aria-label": "Emojis", children: EMOJI_SET.map((emoji) => (_jsx("button", { type: "button", className: "show-chat-panel__emoji-option", role: "option", onClick: () => {
                                        insertEmoji(emoji);
                                    }, children: emoji }, emoji))) })) : null] }), _jsx("input", { className: "fm-input show-chat-panel__input", value: canSend ? messageText : '', maxLength: 500, placeholder: inputPlaceholder, disabled: !canSend || isSending, "aria-disabled": !canSend, onChange: (event) => {
                            if (!canSend) {
                                return;
                            }
                            onMessageTextChange(event.target.value);
                        }, onKeyDown: (event) => {
                            if (!canSend) {
                                return;
                            }
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                onSend();
                            }
                        } }), _jsx("div", { className: "show-chat-panel__send", children: _jsx(Button, { label: isSending ? sendingLabel : sendLabel, disabled: !canSend || isSending || messageText.trim().length === 0, onClick: onSend }) })] }), sendError && canSend ? _jsx("p", { className: "fm-error show-chat-panel__error", children: sendError }) : null] }));
}
//# sourceMappingURL=ShowChatPanel.js.map